<template>
	<v-dialog v-model="open" max-width="720" persistent scrollable>
		<v-card class="round-card background-1">
			<v-card-title class="pa-3">
				<v-icon left>mdi-merge</v-icon>
				<span class="pl-1">Unisci ticket</span>
				<v-spacer></v-spacer>
			</v-card-title>
			<v-divider></v-divider>

			<v-card-text class="pt-4 body-2" style="max-height: 70vh">
				<!-- Selezione insufficiente -->
				<v-alert v-if="selected.length < 2" type="info" text dense class="mb-0">
					Seleziona almeno due ticket dalla lista per poterli unire.
				</v-alert>

				<template v-else>
					<v-alert type="warning" text dense>
						I ticket selezionati verranno uniti nel <strong>ticket principale</strong>.
						I ticket di origine saranno archiviati (consultabili in sola lettura) e tutti i loro
						dati (interventi, email, allegati, note) confluiranno nel principale.
						L'operazione non è reversibile.
					</v-alert>

					<!-- Scelta del ticket principale -->
					<div class="body-1 font-weight-medium mb-2">Scegli il ticket principale</div>
					<v-radio-group v-model="primaryId" class="mt-0" hide-details>
						<v-radio
							v-for="ticket in selected"
							:key="ticket.id"
							:value="ticket.id"
						>
							<template #label>
								<div>
									<span class="font-weight-medium">#{{ ticket.number || ticket.id }}</span>
									<span class="ml-2">{{ ticket.title }}</span>
									<span v-if="ticket.company" class="grey--text ml-2">— {{ ticket.company }}</span>
								</div>
							</template>
						</v-radio>
					</v-radio-group>

					<v-divider class="my-4"></v-divider>

					<!-- Motivi di blocco (autoritativi dal backend) -->
					<v-alert
						v-for="(reason, i) in blocking"
						:key="'block_' + i"
						type="error"
						text
						dense
					>
						{{ reason }}
					</v-alert>

					<!-- Anteprima dati che confluiranno -->
					<div v-if="loadingPreview" class="text-center py-4">
						<v-progress-circular indeterminate color="primary"></v-progress-circular>
					</div>

					<template v-else-if="preview">
						<div class="body-1 font-weight-medium mb-2">
							Verranno spostati nel ticket #{{ preview.principal.number || preview.principal.id }}:
						</div>
						<v-simple-table dense v-if="hasTotals">
							<tbody>
								<tr v-for="(count, key) in nonZeroTotals" :key="key">
									<td>{{ relationLabel(key) }}</td>
									<td class="text-right font-weight-medium">{{ count }}</td>
								</tr>
							</tbody>
						</v-simple-table>
						<div v-else class="grey--text">
							Nessun dato collegato da spostare: verranno uniti solo i ticket.
						</div>
					</template>
				</template>
			</v-card-text>

			<v-divider></v-divider>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn text color="info" @click.native="$emit('close')">Annulla</v-btn>
				<v-btn
					text
					color="primary"
					:loading="loadingMerge"
					:disabled="!canMerge"
					@click.native="performMerge()"
				>
					Unisci
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
export default {
	name: 'merge-tickets-dialog',
	props: {
		params: Object,
		selected: Array,
		selectedIds: Array,
		modelDef: String,
		open: Boolean,
	},
	data() {
		return {
			primaryId: null,
			preview: null,
			blocking: [],
			loadingPreview: false,
			loadingMerge: false,
		}
	},
	computed: {
		sourceIds() {
			return this.selected
				.map(t => t.id)
				.filter(id => id !== this.primaryId)
		},
		nonZeroTotals() {
			if (!this.preview || !this.preview.totals) return {}
			let result = {}
			Object.keys(this.preview.totals).forEach(key => {
				if (this.preview.totals[key] > 0) {
					result[key] = this.preview.totals[key]
				}
			})
			return result
		},
		hasTotals() {
			return Object.keys(this.nonZeroTotals).length > 0
		},
		canMerge() {
			return this.selected.length >= 2 &&
				this.primaryId !== null &&
				this.sourceIds.length > 0 &&
				this.preview !== null &&
				this.blocking.length === 0 &&
				!this.loadingPreview &&
				!this.loadingMerge
		},
	},
	watch: {
		open(value) {
			if (value) {
				this.init()
			}
		},
		primaryId() {
			if (this.open) {
				this.loadPreview()
			}
		},
	},
	methods: {
		init() {
			this.preview = null
			this.blocking = []
			if (this.selected.length >= 2) {
				// Default: primo selezionato come principale. Se il valore non cambia
				// (stessa selezione riaperta, o nuova selezione con lo stesso primo
				// ticket) il watcher su primaryId non scatta: la preview va richiesta
				// esplicitamente, altrimenti resterebbe null e "Unisci" disabilitato.
				const defaultId = this.selected[0].id
				if (this.primaryId === defaultId) {
					this.loadPreview()
				} else {
					this.primaryId = defaultId
				}
			}
		},

		loadPreview() {
			if (!this.primaryId || this.sourceIds.length === 0) {
				return
			}
			this.loadingPreview = true
			this.preview = null
			this.blocking = []
			this.axios.post('helpdesktroubles-merge-preview/' + this.primaryId, {
				source_ids: this.sourceIds
			}).then(response => {
				this.preview = response.data.data
				this.blocking = response.data.data.blocking || []
				this.loadingPreview = false
			}, error => {
				// Senza un'anteprima valida l'unione non deve essere possibile:
				// mantieni preview a null e mostra come blocco il motivo puntuale
				// del backend (422 con message in italiano), se disponibile.
				this.preview = null
				this.blocking = [
					(error && error.response && error.response.data && error.response.data.message) ||
					'Impossibile calcolare l\'anteprima dell\'unione. Riprova.'
				]
				this.loadingPreview = false
			})
		},

		performMerge() {
			this.loadingMerge = true
			this.axios.post('helpdesktroubles-merge/' + this.primaryId, {
				source_ids: this.sourceIds
			}).then(response => {
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: response.data.message || 'Ticket uniti correttamente',
					value: true,
					color: 'success'
				})
				this.loadingMerge = false
				this.$emit('close')
				this.$eventBusUtility.$emit('refreshTable_' + this.modelDef)
			}, error => {
				// Il fallback copre anche risposte senza message (es. body HTML di un 502)
				let message = (error && error.response && error.response.data && error.response.data.message) ||
					'Errore durante l\'unione dei ticket'
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: message,
					value: true,
					color: 'error'
				})
				this.loadingMerge = false
			})
		},

		relationLabel(key) {
			if (this.preview && this.preview.relation_labels && this.preview.relation_labels[key]) {
				return this.preview.relation_labels[key]
			}
			return key
		},
	},
}
</script>
